import React, { useEffect, useRef } from "react";

const CARD = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
const MARKS = [
  { label: "N", deg: 0, major: true },
  { label: "E", deg: 90, major: true },
  { label: "S", deg: 180, major: true },
  { label: "W", deg: 270, major: true },
];

// Lightweight heading indicator. Reads the live facing from a getter (radians)
// on every animation frame and rotates a compass rose without triggering React
// re-renders, so it stays perfectly smooth during look/turn.
export default function Compass({ getHeading }) {
  const roseRef = useRef(null);
  const labelRef = useRef(null);

  useEffect(() => {
    let raf;
    const tick = () => {
      const h = getHeading ? getHeading() || 0 : 0;
      let deg = ((h * 180) / Math.PI) % 360;
      if (deg < 0) deg += 360;
      if (roseRef.current) roseRef.current.style.transform = `rotate(${-deg}deg)`;
      if (labelRef.current) {
        const idx = Math.round(deg / 45) % 8;
        labelRef.current.textContent = `${CARD[idx]} · ${Math.round(deg)}°`;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [getHeading]);

  return (
    <div
      data-testid="hud-compass"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 4,
        pointerEvents: "none",
        userSelect: "none",
      }}
    >
      <div
        style={{
          position: "relative",
          width: 56,
          height: 56,
          borderRadius: 999,
          background: "rgba(10,12,16,0.72)",
          border: "1px solid rgba(201,162,75,0.42)",
          backdropFilter: "blur(10px)",
          boxShadow: "0 6px 20px rgba(0,0,0,0.5)",
        }}
      >
        {/* fixed facing pointer at the top */}
        <div
          style={{
            position: "absolute",
            top: -1,
            left: "50%",
            transform: "translateX(-50%)",
            width: 0,
            height: 0,
            borderLeft: "5px solid transparent",
            borderRight: "5px solid transparent",
            borderTop: "8px solid #f2c85b",
            zIndex: 2,
          }}
        />
        {/* rotating rose */}
        <div
          ref={roseRef}
          style={{
            position: "absolute",
            inset: 0,
            transition: "transform 0.08s linear",
          }}
        >
          {MARKS.map((m) => (
            <span
              key={m.label}
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: `rotate(${m.deg}deg) translateY(-22px) rotate(${-m.deg}deg)`,
                marginLeft: -5,
                marginTop: -7,
                width: 10,
                textAlign: "center",
                fontSize: 9.5,
                fontWeight: 700,
                letterSpacing: "0.02em",
                color: m.label === "N" ? "#f2c85b" : "#9aa0ac",
              }}
            >
              {m.label}
            </span>
          ))}
        </div>
      </div>
      <div
        ref={labelRef}
        className="font-mono-tel hud-compass-label"
        style={{
          fontSize: 10,
          letterSpacing: "0.08em",
          color: "#c9a24b",
          background: "rgba(10,12,16,0.6)",
          padding: "1px 8px",
          borderRadius: 999,
          border: "1px solid rgba(201,162,75,0.28)",
          whiteSpace: "nowrap",
        }}
      >
        N · 0°
      </div>
    </div>
  );
}
